/**
 * VΔcant.day logo — custom ligature where V and Δ share a vertex.
 * V strokes use brand color (theme-aware), Δ strokes use amber accent.
 */

type LogoSize = 'nav' | 'hero' | 'footer' | 'og';

const SIZE_MAP: Record<LogoSize, { height: number; textClass: string; gap: string }> = {
  nav:    { height: 28, textClass: 'text-base',   gap: 'gap-px' },
  hero:   { height: 48, textClass: 'text-2xl',    gap: 'gap-0.5' },
  footer: { height: 24, textClass: 'text-detail',  gap: 'gap-px' },
  og:     { height: 72, textClass: 'text-4xl',    gap: 'gap-1' },
};

/** The VΔ ligature mark SVG — standalone for favicon/icon use */
export function LogoMark({
  height = 28,
  className = '',
}: {
  height?: number;
  className?: string;
}) {
  const aspectRatio = 80 / 62;
  const width = Math.round(height * aspectRatio);

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 80 62"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      {/* V strokes — brand color (inherits currentColor) */}
      <line x1="4" y1="4" x2="28" y2="56" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <line x1="28" y1="56" x2="44" y2="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Δ strokes — amber accent */}
      <line x1="36" y1="56" x2="60" y2="4" stroke="var(--accent-warm, #c07840)" strokeWidth="2" strokeLinecap="round" />
      <line x1="60" y1="4" x2="76" y2="56" stroke="var(--accent-warm, #c07840)" strokeWidth="5" strokeLinecap="round" />
      <line x1="36" y1="56" x2="76" y2="56" stroke="var(--accent-warm, #c07840)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Full wordmark: VΔ ligature + "cant.day" */
export function Logo({
  size = 'nav',
  className = '',
}: {
  size?: LogoSize;
  className?: string;
}) {
  const { height, textClass, gap } = SIZE_MAP[size];

  return (
    <span className={`inline-flex items-baseline ${gap} ${className}`} aria-label="vacant.day">
      {/* sr-only "va" so crawlers and AI assistants read the full brand name
       * — the VΔ ligature is rendered as SVG and is invisible to text scrapers. */}
      <span className="sr-only">va</span>
      <LogoMark height={height} className="text-brand self-center" />
      <span className={`font-heading font-bold text-text-primary ${textClass} leading-none`}>
        cant
      </span>
      <span className={`font-heading font-bold ${textClass} leading-none`} style={{ color: 'var(--accent-warm, #c07840)' }}>
        .
      </span>
      <span className={`font-heading font-normal text-text-primary ${textClass} leading-none`}>
        day
      </span>
    </span>
  );
}
